// OtpInput — fixed-length one-time-code entry as separate boxes. Typing a
// digit auto-advances; backspace steps back; paste distributes across boxes.
// Forced LTR so the code order never flips under RTL.

import { useRef, type ClipboardEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

export interface OtpInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  autoFocus?: boolean;
  className?: string;
}

const OtpInput = ({
  length = 4,
  value,
  onChange,
  error = false,
  autoFocus = false,
  className = '',
}: OtpInputProps) => {
  const { t } = useTranslation();
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  const focusBox = (i: number) => {
    const el = refs.current[Math.max(0, Math.min(length - 1, i))];
    el?.focus();
    el?.select();
  };

  const setDigit = (i: number, digit: string) => {
    const next = digits.slice();
    next[i] = digit;
    onChange(next.join('').slice(0, length));
  };

  const handleChange = (i: number, raw: string) => {
    const cleaned = raw.replace(/\D/g, '');
    if (!cleaned) {
      setDigit(i, '');
      return;
    }
    setDigit(i, cleaned[cleaned.length - 1]);
    if (i < length - 1) focusBox(i + 1);
  };

  const handleKeyDown = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      setDigit(i - 1, '');
      focusBox(i - 1);
    } else if (e.key === 'ArrowLeft' && i > 0) {
      focusBox(i - 1);
    } else if (e.key === 'ArrowRight' && i < length - 1) {
      focusBox(i + 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    onChange(pasted);
    focusBox(Math.min(pasted.length, length - 1));
  };

  const classes = ['app-otp', error && 'app-otp--error', className].filter(Boolean).join(' ');

  return (
    <div className={classes} dir='ltr'>
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          className='app-otp__box'
          type='text'
          inputMode='numeric'
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={digit}
          autoFocus={autoFocus && i === 0}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          aria-label={t('aria_otp_digit', { number: i + 1 })}
          aria-invalid={error || undefined}
        />
      ))}
    </div>
  );
};

export default OtpInput;
